// ObjectGenerator — AI prop/object creation for the world.
// Flow: idea → (prompt assist) → Muse image (Nano Banana Pro fallback) → preview →
//       Meshy image-to-3D → GLB persisted to S3 → placed in world.
import React, { useState, useCallback } from 'react';
import { X, Sparkles, Loader, CheckCircle, AlertCircle, Wand2, RefreshCw, ArrowLeft, Box } from 'lucide-react';
import { putObject } from '../utils/uploadToStorage';
import { generateObjectImage, enhancePrompt } from '../services/openRouterApi';
import { generateObjectFromImage } from '../services/meshyApi';
import './ObjectGenerator.css';

interface ObjectGeneratorProps {
  // Returns the persisted GLB URL + a short name; WorldBuilder places it.
  onObjectCreated: (glbUrl: string, name: string, thumbnailUrl?: string) => void;
  onClose: () => void;
}

type Step = 'design' | 'generating-image' | 'preview' | 'converting' | 'done' | 'error';

const EXAMPLES = [
  'a weathered wooden treasure chest with iron bands',
  'a glowing neon arcade machine',
  'a potted monstera plant',
  'a medieval iron lantern',
  'a vintage red telephone booth',
  'a mossy stone fountain',
];

function dataUrlToBytes(dataUrl: string): Uint8Array {
  const base64 = dataUrl.split(',')[1] || '';
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

const ObjectGenerator: React.FC<ObjectGeneratorProps> = ({ onObjectCreated, onClose }) => {
  const [step, setStep] = useState<Step>('design');
  const [idea, setIdea] = useState('');
  const [enhancing, setEnhancing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stageLabel, setStageLabel] = useState('');
  const [error, setError] = useState('');
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);

  const uploadToS3 = async (data: Uint8Array | Blob, key: string, contentType: string): Promise<string> => {
    return putObject(key, data, contentType);
  };

  const persistGlbToS3 = async (meshyGlbUrl: string): Promise<string> => {
    const res = await fetch('/api/proxy-upload', { method: 'POST', headers: { 'x-fetch-url': meshyGlbUrl } });
    if (!res.ok) throw new Error('Failed to download generated object');
    const blob = await res.blob();
    return uploadToS3(blob, `objects/models/${Date.now()}-object.glb`, 'model/gltf-binary');
  };

  const handleEnhance = useCallback(async () => {
    if (!idea.trim() || enhancing) return;
    setEnhancing(true);
    try {
      const improved = await enhancePrompt(idea, 'object');
      setIdea(improved);
    } finally {
      setEnhancing(false);
    }
  }, [idea, enhancing]);

  const handleGenerateImage = useCallback(async () => {
    if (!idea.trim()) return;
    setError('');
    setStep('generating-image');
    try {
      const img = await generateObjectImage(idea);
      setGeneratedImage(img.dataUrl);
      setStep('preview');
    } catch (e: any) {
      setError(e?.message || 'Image generation failed');
      setStep('error');
    }
  }, [idea]);

  const handleConvert = useCallback(async () => {
    if (!generatedImage) return;
    setError('');
    setStep('converting');
    setProgress(0);
    try {
      // 1. Upload the reference image so Meshy can fetch it by URL
      setStageLabel('Uploading reference');
      const refUrl = await uploadToS3(dataUrlToBytes(generatedImage), `objects/references/${Date.now()}.png`, 'image/png');

      // 2. Image → 3D (no rigging)
      const { glbUrl, thumbnailUrl } = await generateObjectFromImage(refUrl, {
        onStage: (s) => setStageLabel(s === 'generating' ? 'Building 3D model' : 'Finishing'),
        onProgress: (p) => setProgress(Math.round(p * 0.85)),
      });

      // 3. Persist GLB to our storage (Meshy URLs expire)
      setStageLabel('Saving to your world');
      setProgress(90);
      const persisted = await persistGlbToS3(glbUrl);
      let thumb: string | undefined;
      if (thumbnailUrl) {
        try {
          const r = await fetch('/api/proxy-upload', { method: 'POST', headers: { 'x-fetch-url': thumbnailUrl } });
          if (r.ok) thumb = await uploadToS3(await r.blob(), `objects/thumbnails/${Date.now()}.png`, 'image/png');
        } catch { /* thumbnail is optional */ }
      }
      setProgress(100);

      const name = idea.trim().split(/[,.]/)[0].slice(0, 40) || 'AI Object';
      setStep('done');
      setTimeout(() => onObjectCreated(persisted, name, thumb), 600);
    } catch (e: any) {
      setError(e?.message || '3D conversion failed');
      setStep('error');
    }
  }, [generatedImage, idea, onObjectCreated]);

  return (
    <div className="og-overlay" onClick={onClose}>
      <div className="og-modal" onClick={(e) => e.stopPropagation()}>
        <button className="og-close" onClick={onClose}><X size={18} /></button>
        <div className="og-header">
          <Box size={18} />
          <h2>Generate an Object</h2>
        </div>

        {step === 'design' && (
          <div className="og-body">
            <label className="og-label">Describe the object</label>
            <div className="og-input-wrap">
              <textarea
                className="og-textarea"
                value={idea}
                onChange={(e) => setIdea(e.target.value)}
                placeholder="e.g. a weathered wooden treasure chest with iron bands"
                rows={3}
              />
              <button className="og-enhance" onClick={handleEnhance} disabled={!idea.trim() || enhancing} title="Improve this prompt with AI">
                {enhancing ? <Loader size={14} className="og-spin" /> : <Wand2 size={14} />}
                {enhancing ? 'Enhancing…' : 'Enhance'}
              </button>
            </div>
            <div className="og-examples">
              {EXAMPLES.map((ex) => (
                <button key={ex} className="og-chip" onClick={() => setIdea(ex)}>{ex}</button>
              ))}
            </div>
            <button className="og-primary" onClick={handleGenerateImage} disabled={!idea.trim()}>
              <Sparkles size={16} /> Generate Image
            </button>
            <p className="og-hint">Makes a reference image with Muse, then Meshy builds the 3D model.</p>
          </div>
        )}

        {step === 'generating-image' && (
          <div className="og-body og-center">
            <Loader size={40} className="og-spin" />
            <p>Generating image…</p>
          </div>
        )}

        {step === 'preview' && generatedImage && (
          <div className="og-body">
            <div className="og-preview"><img src={generatedImage} alt="generated object" /></div>
            <p className="og-caption">Happy with this? We'll turn it into a 3D model.</p>
            <div className="og-actions">
              <button className="og-secondary" onClick={() => setStep('design')}><ArrowLeft size={14} /> Edit</button>
              <button className="og-secondary" onClick={handleGenerateImage}><RefreshCw size={14} /> Regenerate</button>
              <button className="og-primary" onClick={handleConvert}><Box size={16} /> Make 3D Object</button>
            </div>
          </div>
        )}

        {step === 'converting' && (
          <div className="og-body og-center">
            <Loader size={40} className="og-spin" />
            <p>{stageLabel || 'Working…'}</p>
            <div className="og-progress"><div className="og-progress-bar" style={{ width: `${progress}%` }} /></div>
            <span className="og-progress-pct">{progress}%</span>
          </div>
        )}

        {step === 'done' && (
          <div className="og-body og-center">
            <CheckCircle size={44} className="og-success" />
            <p>Object created! Placing it in your world…</p>
          </div>
        )}

        {step === 'error' && (
          <div className="og-body og-center">
            <AlertCircle size={44} className="og-error-icon" />
            <p className="og-error-text">{error}</p>
            <button className="og-secondary" onClick={() => setStep('design')}>Start Over</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ObjectGenerator;
